"use client";
import { useState } from "react";
import {
  SimpleListConfigurable,
  List,
  TextField,
  ReferenceField,
  FunctionField,
  TopToolbar,
  FilterButton,
  SelectColumnsButton,
  DatagridConfigurable,
  useListContext,
  WrapperField,
  ArrayField,
  SingleFieldList,
  ChipField,
} from "react-admin";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { RaRowActions } from "../../components/RaRowActions";
import { CategoryChip } from "../../components/CategoryChip";
import { Button, Chip, Tooltip } from "@mui/material";
import "moment/locale/pt-br";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { HiOutlineX } from "react-icons/hi";
import { PostFilterSidebar } from "./DateFilterSidebar";
import { postFilters } from "./TopBarFilters";
import { PostBulkActionButtons } from "./ModalBulkActions";
import { determineUserReadableRole, truncateString } from "./UserUtils";
import { useLocation } from "react-router-dom";
import { CustomExportButton } from "./CustomExport";
import Alert from "@mui/material/Alert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { UserListSocioColumn } from "./UserListSocioColumn";
import { UserListDateColumn } from "./UserListDateColumn";
import { colors, radius, shadows } from "../../data/tokens";

// Matrícula formatada — sem número cadastrado exibe "Não" (mesma
// linguagem do filtro "Possui Matrícula"); antes o null do banco era
// concatenado cru e aparecia "null-null" na tela
const formatRegistration = (record: any) => {
  const num = record?.registrationNumber;
  const digit = record?.registrationNumberDigit;
  if (num === null || num === undefined || num === "") return "Não";
  if (digit === null || digit === undefined || digit === "") {
    return String(num);
  }
  return `${num}-${digit}`;
};

// Estilo compacto compartilhado pelas duas tabelas (admin e secretaria)
const datagridSx = {
  "& .RaDatagrid-headerCell": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
};

// Limpa TODOS os filtros ativos (inclusive os de data) e volta a
// ordenação padrão; só aparece quando existe filtro aplicado
const ClearAllFiltersButton = () => {
  const { filterValues, setFilters, setSort } = useListContext();
  if (!filterValues || Object.keys(filterValues).length === 0) return null;
  return (
    <Button
      size="small"
      color="secondary"
      startIcon={<HiOutlineX size={16} />}
      onClick={() => {
        setFilters({}, []);
        localStorage.removeItem("dateField");
        setSort({ field: "createdAt", order: "DESC" });
      }}
    >
      Limpar filtros
    </Button>
  );
};

export const UserList = () => {
  // Painel de filtro/ordenação por datas inicia fechado (ganha ~150px);
  // o botão "Filtro de Datas" da toolbar alterna
  const [showSidebar, setShowSidebar] = useState(false);
  const userRole = localStorage.getItem("roles");
  const location = useLocation();
  const isMobile = localStorage.getItem("isMobile") === "true";

  // if (isLoading) return null;
  // console.log("test data", data);

  // const setHasPhysicalCard = { hasPhysicalCard: true };
  // const [hasPhysicalCard, setHasPhysicalCard] = useState<any>({
  //   hasPhysicalCard: true,
  // });

  const ListActions = () => {
    return (
      <TopToolbar sx={{ alignItems: "center" }}>
        {!isMobile && <SelectColumnsButton />}

        <FilterButton />
        {/* <CreateButton /> */}
        {/* <ExportButton /> */}
        {isMobile ? (
          <Button size="small" onClick={() => setShowSidebar(!showSidebar)}>
            {showSidebar ? <ToggleOnIcon /> : <ToggleOffIcon />}
          </Button>
        ) : (
          <Button
            size="small"
            startIcon={showSidebar ? <ToggleOnIcon /> : <ToggleOffIcon />}
            onClick={() => setShowSidebar(!showSidebar)}>
            Filtro de Datas
          </Button>
        )}
        {(determineUserReadableRole(userRole) === "admin" ||
          determineUserReadableRole(userRole) === "comms") && (
          <CustomExportButton location={location} />
        )}
        <ClearAllFiltersButton />
        {/* navegação ancorada à direita; botões ficam à esquerda */}
        <span style={{ marginLeft: "auto", display: "flex" }}>
          <ListPageIndicator />
        </span>
      </TopToolbar>
    );
  };

  return (
    <>
      <PageTopBreadcrumb
        title="Sócios"
        firstPath="/#/users/crud"
        actionType="create"
      />
      <List
        title=" "
        actions={<ListActions />}
        filters={postFilters}
        // Mantém a página atual visível enquanto a próxima carrega
        queryOptions={{ keepPreviousData: true }}
        sx={{
          // Busca, tabela e paginação dentro do mesmo cartão branco
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "12px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          // Toolbar em pilha: cluster de ações no topo (à direita) e o
          // formulário de filtros embaixo em linhas limpas — antes ambos
          // dividiam a mesma linha flex e, com vários filtros, o cluster
          // flutuava no meio abrindo vãos
          "& .RaList-actions": {
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "stretch",
            gap: 1,
          },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            minHeight: "auto !important",
            paddingBottom: 0,
          },
          // O span vazio entre botões e filtros dobrava o gap da pilha
          "& .RaList-actions > span:empty": { display: "none" },
          // Filtros em linha única elástica: cada filtro cresce/encolhe
          // para caber na largura; campos e botões ⊗ centralizados e
          // vão vertical mínimo quando precisar quebrar linha
          "& .RaList-actions form": {
            width: "100%",
            alignItems: "center",
            // gap vertical = horizontal (12px) para grade uniforme
            rowGap: 1.5,
            columnGap: 1.5,
            paddingBottom: 1,
            paddingTop: 0,
            minHeight: "auto",
          },
          "& .RaFilterForm-filterFormInput": {
            display: "flex",
            alignItems: "center",
            // position: ancora o botão ⊗ no canto superior direito
            position: "relative",
            marginTop: "0 !important",
            marginBottom: "0 !important",
            // Largura única para todos os filtros (padrão e adicionados):
            // grade regular de ~240px, sem esticar para preencher
            flex: "0 1 240px",
            minWidth: 170,
          },
          "& .RaFilterForm-filterFormInput .MuiFormControl-root": {
            width: "100%",
            marginTop: "0 !important",
            marginBottom: "0 !important",
          },
          // Botão ⊗ como "badge" no canto superior direito do campo,
          // com fundo branco para destacar da borda e vermelho no hover
          "& .RaFilterFormInput-hideButton": {
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 1,
            padding: 0,
            marginTop: "0 !important",
            marginBottom: "0 !important",
            color: colors.textMuted,
            backgroundColor: colors.white,
            borderRadius: "50%",
            "& svg": { fontSize: 22 },
            "&:hover": {
              color: colors.action,
              backgroundColor: colors.white,
            },
          },
          "& .RaFilterFormInput-spacer": { display: "none" },
        }}>
                {showSidebar &&
                (determineUserReadableRole(userRole) === "admin" ||
                  determineUserReadableRole(userRole) === "finance" ||
                  determineUserReadableRole(userRole) === "secretary" ||
                  determineUserReadableRole(userRole) === "comms") ? (
                  <PostFilterSidebar />
                ) : (
                  <></>
                )}
                {isMobile ? (
                  <SimpleListConfigurable
                    primaryText={(record) => record.fullName}
                    secondaryText={(record) =>
                      `${record.email ? record.email : ""}`
                    }
                    tertiaryText={(record) =>
                      `M.: ${formatRegistration(record)}`
                    }
                  />
                ) : (
                  <>
                    {(determineUserReadableRole(userRole) === "admin" ||
                      determineUserReadableRole(userRole) === "finance") && (
                      <DatagridConfigurable
                        sx={datagridSx}
                        bulkActionButtons={
                          determineUserReadableRole(userRole) === "admin" ? (
                            <PostBulkActionButtons />
                          ) : (
                            false
                          )
                        }>
                        <WrapperField label="Sócio">
                          <UserListSocioColumn />
                        </WrapperField>

                        <ArrayField label="Formações" source="userGraduations">
                          <SingleFieldList linkType={false}>
                            {/* <ChipField source="graduation.name" size="small" /> */}
                            <FunctionField
                              label="Formações"
                              render={(record: any) => {
                                // console.log("RENDERREQ", record.graduation.name);
                                return (
                                  <>
                                    {record &&
                                      record.hasOwnProperty("graduation") &&
                                      record.graduation.hasOwnProperty(
                                        "name"
                                      ) && (
                                        <>
                                          <Tooltip
                                            placement="right"
                                            title={
                                              record.graduation.name +
                                              " - " +
                                              record.yearCompletion +
                                              " - " +
                                              record.educationalInstitution.name
                                            }>
                                            <Chip
                                              size="small"
                                              label={truncateString(
                                                record.graduation.name +
                                                  " - " +
                                                  record.yearCompletion,
                                                23
                                              )}
                                              color="default"
                                            />
                                          </Tooltip>
                                        </>
                                      )}
                                  </>
                                );
                              }}
                            />
                          </SingleFieldList>
                        </ArrayField>
                        <ArrayField label="DTEs" source="technicalDivisions">
                          <SingleFieldList linkType={false}>
                            <ChipField source="name" size="small" />
                          </SingleFieldList>
                        </ArrayField>
                        {/* <FunctionField
                      label="Matrícula"
                      render={(record: any) => {
                        return (
                          <>
                            {record.registrationNumber +
                              "-" +
                              record.registrationNumberDigit}
                          </>
                        );
                      }}
                      /> */}
                        <FunctionField
                          label="Matrícula"
                          sortBy="registrationNumber"
                          render={(record: any) => formatRegistration(record)}
                        />
                        {/* <TextField source="registrationNumber" label="Matrícula" /> */}
                        <FunctionField
                          label="Datas"
                          render={(record: any) => {
                            return <UserListDateColumn record={record} />;
                          }}
                        />

                        <ReferenceField
                          source="userCategoryId"
                          reference="user-categories/crud"
                          label="Categoria"
                          textAlign="center">
                          <FunctionField
                            render={(cat: any) => (
                              <CategoryChip name={cat?.name} />
                            )}
                          />
                        </ReferenceField>
                        {/* <FunctionField
                      label="Categoria"
                      render={(record: any) => {
                        return record.CatSocio === "9" ? "Remido" : "Aspirante";
                      }}
                      /> */}
                        <FunctionField
                          label="Situação"
                          textAlign="center"
                          render={(record: any) => {
                            switch (record.accountStatus) {
                              case "ACTIVE":
                                return <Chip size="small" label="Ativo" color="success" />;
                              case "CANCELED":
                                return <Chip size="small" label="Cancelado" color="error" />;
                              case "NO_CONTACT":
                                return (
                                  <Chip
                                    size="small"
                                    label="Sem contato"
                                    color="warning"
                                  />
                                );
                              case "PENDING":
                                return (
                                  <Chip
                                    size="small"
                                    label="Pendente"
                                    color="warning"
                                  />
                                );
                              case "LICENSED":
                                return (
                                  <Chip
                                    size="small"
                                    label="Licenciado"
                                    color="warning"
                                  />
                                );
                              case "DECEASED":
                                return <Chip size="small" label="Falecido" />;
                              default:
                                return (
                                  <Chip
                                    size="small"
                                    label="Desconhecido"
                                    variant="outlined"
                                  />
                                );
                            }
                          }}
                        />

                        <FunctionField
                          label="Adimplência"
                          textAlign="center"
                          sortBy="userEnabled"
                          render={(record: any) => (
                            <Tooltip
                              title={
                                record.userEnabled
                                  ? "Adimplente"
                                  : "Inadimplente"
                              }
                            >
                              {record.userEnabled ? (
                                <CheckCircleOutlineIcon
                                  fontSize="small"
                                  color="success"
                                />
                              ) : (
                                <HighlightOffIcon
                                  fontSize="small"
                                  color="error"
                                />
                              )}
                            </Tooltip>
                          )}
                        />
                        <TextField
                          label="Última Contribuição"
                          source="userLastContribution"
                        />
                        {determineUserReadableRole(userRole) === "admin" && (
                          <WrapperField label="Ações" textAlign="right">
                            <RaRowActions />
                          </WrapperField>
                        )}
                      </DatagridConfigurable>
                    )}
                    {(determineUserReadableRole(userRole) === "comms" ||
                      determineUserReadableRole(userRole) === "secretary") && (
                      <DatagridConfigurable
                        sx={datagridSx}
                        bulkActionButtons={false}
                        rowClick={
                          determineUserReadableRole(userRole) === "comms"
                            ? ""
                            : "show"
                        }>
                        <WrapperField label="Sócio">
                          <UserListSocioColumn />
                        </WrapperField>
                        {determineUserReadableRole(userRole) ===
                          "secretary" && (
                          <FunctionField
                            label="Matrícula"
                            sortBy="registrationNumber"
                            render={(record: any) =>
                              formatRegistration(record)
                            }
                          />
                        )}

                        <FunctionField
                          label="Datas"
                          render={(record: any) => {
                            return <UserListDateColumn record={record} />;
                          }}
                        />
                        <ReferenceField
                          source="userCategoryId"
                          reference="user-categories/crud"
                          label="Categoria"
                          textAlign="center">
                          <FunctionField
                            render={(cat: any) => (
                              <CategoryChip name={cat?.name} />
                            )}
                          />
                        </ReferenceField>

                        <FunctionField
                          label="Situação"
                          textAlign="center"
                          render={(record: any) => {
                            switch (record.accountStatus) {
                              case "ACTIVE":
                                return <Chip size="small" label="Ativo" color="success" />;
                              case "CANCELED":
                                return <Chip size="small" label="Cancelado" color="error" />;
                              case "NO_CONTACT":
                                return (
                                  <Chip
                                    size="small"
                                    label="Sem contato"
                                    color="warning"
                                  />
                                );
                              case "PENDING":
                                return (
                                  <Chip
                                    size="small"
                                    label="Pendente"
                                    color="warning"
                                  />
                                );
                              case "LICENSED":
                                return (
                                  <Chip
                                    size="small"
                                    label="Licenciado"
                                    color="warning"
                                  />
                                );
                              case "DECEASED":
                                return <Chip size="small" label="Falecido" />;
                              default:
                                return (
                                  <Chip
                                    size="small"
                                    label="Desconhecido"
                                    variant="outlined"
                                  />
                                );
                            }
                          }}
                        />
                        {determineUserReadableRole(userRole) ===
                          "secretary" && (
                          <FunctionField
                            label="Adimplência"
                            textAlign="center"
                            sortBy="userEnabled"
                            render={(record: any) => (
                              <Tooltip
                                title={
                                  record.userEnabled
                                    ? "Adimplente"
                                    : "Inadimplente"
                                }
                              >
                                {record.userEnabled ? (
                                  <CheckCircleOutlineIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                ) : (
                                  <HighlightOffIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                )}
                              </Tooltip>
                            )}
                          />
                        )}
                        {determineUserReadableRole(userRole) ===
                          "secretary" && (
                          <TextField
                            label="Última Contribuição"
                            source="userLastContribution"
                          />
                        )}
                      </DatagridConfigurable>
                    )}
                  </>
                )}
      </List>
      {location.search.includes("userEnabled") && (
        <Alert
          sx={{ marginTop: 2 }}
          icon={<WarningAmberIcon fontSize="inherit" />}
          severity="info">
          A lista de membros adimplentes/inadimplentes atualizam em background
          a cada 3 minutos e armazenadas em cache, alterações que afetem
          adimplência podem levar até 3 min para surtir efeito na visualização
          desta lista de Sócios. (Este cache não afeta exportação de membros,
          mediante a exportação os dados são solicitados em tempo real.)
        </Alert>
      )}
    </>
  );
};
